import { useEffect, useMemo, useState } from "react";
import { RadioButton } from "primereact/radiobutton";
import { AppDialog } from "../AppDialog/AppDialog";
import { DefaultButton } from "../button/defaultButton/DefaultButton";
import { RenderWhen } from "../../infrastructure/components/RenderWhen";
import { useApiQuery } from "../../infrastructure/hooks/useApiQuery";
import { PaymentTypes, PrepaymentProvider } from "../../enums";
import { PaymentTypesLabels } from "../../labels";
import { IEligiblePlansResponse } from "./eligiblePlansModel";
import styles from "./PrepaymentDialog.module.scss";

const CARD_VALUE = "card";
const MULTISPORT_VALUE = "multisport";
const OTHER_VALUE = "other";

export interface IPrepaymentDialogConfirmResult {
  subscriptionPlanId: number | null;
  payWithMultisport: boolean;
}

interface IPrepaymentDialogProps {
  visible: boolean;
  /**
   * Where to GET the eligible plans payload from. The caller builds the full
   * URL including its own identifier (`userId` for the authenticated portal-calendar
   * flow, `email` for the anonymous waitlist flow), so the dialog stays
   * endpoint-agnostic and works across contexts.
   */
  eligiblePlansEndpoint?: string;
  /** React-Query key for the fetch; bumps whenever the caller's context changes. */
  eligiblePlansQueryKey: readonly unknown[];
  /** Price label shown above the radio group. */
  price: number;
  /** When Link-prepaid, the dialog adds the "you'll get an email" sentence. */
  prepaymentProvider?: PrepaymentProvider;
  isDesktopOrTablet: boolean;
  /**
   * False when the dialog is only shown to capture the payment method (Multisport) on a
   * class that does NOT require prepayment — in that case there is no card/Stripe/Link
   * copy, and "Кредитна/дебитна карта" is replaced with a generic "pay on site" option.
   */
  isPrepaid: boolean;
  /** True adds the Multisport radio option, regardless of prepayment requirement. */
  allowMultisport?: boolean;
  onConfirm: (result: IPrepaymentDialogConfirmResult) => void;
  onCancel: () => void;
}

const formatVisits = (remaining: number | null): string => {
  if (remaining === null) {
    return "неограничен брой посещения";
  }
  const noun = remaining === 1 ? "посещение" : "посещения";
  return `${remaining} ${noun}`;
};

export const PrepaymentDialog = ({
  visible,
  eligiblePlansEndpoint,
  eligiblePlansQueryKey,
  price,
  prepaymentProvider,
  isDesktopOrTablet,
  isPrepaid,
  allowMultisport,
  onConfirm,
  onCancel,
}: IPrepaymentDialogProps) => {
  const canFetch = visible && !!eligiblePlansEndpoint;

  const { data, isLoading } = useApiQuery<IEligiblePlansResponse>({
    queryKey: [...eligiblePlansQueryKey],
    endpoint: eligiblePlansEndpoint ?? "",
    enabled: canFetch,
    globalIsLoading: false,
  });

  const plans = useMemo(() => data?.plans ?? [], [data]);

  const defaultOtherValue = isPrepaid ? CARD_VALUE : OTHER_VALUE;

  // First eligible plan is the default. Multisport is never pre-selected — the customer
  // must actively opt into it.
  const [selection, setSelection] = useState<string>(defaultOtherValue);

  useEffect(() => {
    if (!visible) {
      return;
    }
    setSelection(plans.length > 0 ? String(plans[0].id) : defaultOtherValue);
  }, [visible, plans, defaultOtherValue]);

  const handleConfirm = () => {
    if (selection === MULTISPORT_VALUE) {
      onConfirm({ subscriptionPlanId: null, payWithMultisport: true });
    } else if (selection === CARD_VALUE || selection === OTHER_VALUE) {
      onConfirm({ subscriptionPlanId: null, payWithMultisport: false });
    } else {
      const id = Number(selection);
      onConfirm({
        subscriptionPlanId: Number.isFinite(id) ? id : null,
        payWithMultisport: false,
      });
    }
  };

  return (
    <AppDialog
      visible={visible}
      onHide={onCancel}
      header={isPrepaid ? "Предплащане" : "Начин на плащане"}
      style={{ width: isDesktopOrTablet ? "500px" : "92vw" }}
    >
      <div className={styles.body}>
        <RenderWhen condition={isPrepaid}>
          <p>За това събитие се изисква предплащане.</p>

          <RenderWhen
            condition={prepaymentProvider === PrepaymentProvider.Link}
          >
            <p>
              След като направите резервация, ще получите имейл с линк за плащане.
            </p>
            <p>
              Резервацията Ви ще бъде потвърдена след получаване на плащането.
            </p>
          </RenderWhen>
        </RenderWhen>

        <RenderWhen condition={!isPrepaid}>
          <p>Изберете начин на плащане за резервацията.</p>
        </RenderWhen>

        <p>
          Сума за плащане:{" "}
          <strong>{(price ?? 0).toFixed(2)} EUR</strong>
        </p>

        <div className={styles.section}>
          <p className={styles.section_title}>Начин на плащане:</p>

          <RenderWhen condition={isLoading}>
            <p className={styles.loading}>Зареждане на абонаментните Ви планове…</p>
          </RenderWhen>

          <RenderWhen condition={!isLoading}>
            <div className={styles.options}>
              {plans.map((plan) => {
                const value = String(plan.id);
                return (
                  <label key={plan.id} className={styles.option}>
                    <RadioButton
                      inputId={`prepayment-plan-${plan.id}`}
                      name="prepayment-method"
                      value={value}
                      onChange={(e) => setSelection(e.value as string)}
                      checked={selection === value}
                    />
                    <span>
                      Заплащане с активен абонаментен план — {plan.name} —{" "}
                      {formatVisits(plan.remainingVisits)}
                    </span>
                  </label>
                );
              })}

              <RenderWhen condition={!!allowMultisport}>
                <label className={styles.option}>
                  <RadioButton
                    inputId="prepayment-multisport"
                    name="prepayment-method"
                    value={MULTISPORT_VALUE}
                    onChange={(e) => setSelection(e.value as string)}
                    checked={selection === MULTISPORT_VALUE}
                  />
                  <span>{PaymentTypesLabels[PaymentTypes.Multisport]}</span>
                </label>
              </RenderWhen>

              <RenderWhen condition={isPrepaid}>
                <label className={styles.option}>
                  <RadioButton
                    inputId="prepayment-card"
                    name="prepayment-method"
                    value={CARD_VALUE}
                    onChange={(e) => setSelection(e.value as string)}
                    checked={selection === CARD_VALUE}
                  />
                  <span>Кредитна/дебитна карта</span>
                </label>
              </RenderWhen>

              <RenderWhen condition={!isPrepaid}>
                <label className={styles.option}>
                  <RadioButton
                    inputId="prepayment-other"
                    name="prepayment-method"
                    value={OTHER_VALUE}
                    onChange={(e) => setSelection(e.value as string)}
                    checked={selection === OTHER_VALUE}
                  />
                  <span>Ще платя на място / друго</span>
                </label>
              </RenderWhen>
            </div>
          </RenderWhen>
        </div>

        <div className={styles.actions}>
          <DefaultButton
            label="Продължи"
            design="secondary"
            width="auto"
            onClick={handleConfirm}
            disabled={isLoading}
          />
          <DefaultButton
            label="Отказ"
            design="outline"
            width="auto"
            onClick={onCancel}
          />
        </div>
      </div>
    </AppDialog>
  );
};
